// Veri Girişi grid'i (M16 T10, mockup 02; D6 accordion, D7 klavye, kural 3 kolon genişlikleri,
// kural 12 highlight). Saf mantık lib/kpi-grid-nav.ts'te; burada yalnız render + olaylar.
// Hücre: tıkla/Enter → KpiCellEditor; türetilmiş → FormulaPopover (rev-B 3: hover/odak → hafif
// FormulaHoverCard, use-formula-hover.tsx); aggregate kolon salt-okunur. T18: ataç rozeti → editör
// Kanıt sekmesi; T23: RAPORLANAN/SABİT rozeti → ReportedPopover. Satır render'ı KpiGridRow.tsx'te.
// `role="treegrid"` + aria-rowindex + görünür focus halkası (kural 17); sayfa yatay scroll YASAK —
// tablo kendi `overflow-x:auto` kabında; kap dikeyde ebeveyn flex sütununu doldurur. Popover'lar
// `document.body`'ye portal'lanır (`use-popover-placement.ts`); çapa `cellRefs`'ten her ölçümde
// okunur. TODO-KPI-05 yapıştırma: Ctrl/⌘+V → `use-grid-paste.ts` → KpiPasteDialog → `onSaveCells`
// → PasteSnackbar "Geri al". TODO-KPI-07 sanallaştırma (`use-virtual-rows.ts`, >80 satır, satır
// türüne göre yükseklik, doldurucu `<tr>`); TODO-KPI-06/K9 ad kolonu 220/160 px. K10 yalnız-not editörü.
// Dalga 3: `notes`, `clientView.totalColumns`, Y4a; DR9 `clientView.unitChanged` → editör amber satırı.
// Kırılım (spec §6.1): toplam satırında ilk kolonda ←/→ aç/kapa (`totalToggleFor`), grup bandı atlanır;
// §6.2 kırılım bölümü + bekleme + toplama yazma ipucu `grid-breakdown-props.ts` üzerinden.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { KpiFormMismatch, KpiResolvedGrid } from "@mahir/shared";
import {
  buildColumns,
  buildRows,
  formulaInputsFor,
  gridRowHeights,
  isEditable,
  isGridCellKeyTarget,
  navReducer,
  readCell,
  totalKindOf,
  totalToggleFor,
  type CellInput,
  type CellPos,
  type GridKey,
  type GridViewMode,
  type NavState,
} from "../../lib/kpi-grid-nav";
import { FormulaPopover } from "./FormulaPopover";
import {
  breakdownHintFor,
  breakdownPropsFor,
  type GridBreakdownHandlers,
} from "./grid-breakdown-props";
import { KpiCellEditor, type CellEditorTab } from "./KpiCellEditor";
import { canNoteAt, KpiCellNoteEditor } from "./KpiCellNoteEditor";
import { KpiGridBody } from "./KpiGridBody";
import type { GridClientView } from "./KpiGridRow";
import { KpiPasteDialog, PasteSnackbar } from "./KpiPasteDialog";
import { ReportedPopover } from "./ReportedPopover";
import { useFormulaHover } from "./use-formula-hover";
import { useGridPaste, type PasteSaveItem } from "./use-grid-paste";
import { useVirtualRows } from "./use-virtual-rows";
import { type GridNotes } from "../../lib/kpi-notes-ui";
import { buildPartialLookup } from "../../lib/kpi-partial-info";
import { KPI_COPY } from "../../lib/kpi-ui";
import { wavgPopoverPairs } from "../../lib/kpi-wavg-pairs";
import { useMediaQuery } from "../../lib/use-media-query";

export interface KpiGridProps {
  grid: KpiResolvedGrid;
  mode: GridViewMode;
  entityYear: number;
  search: string;
  categoryId: string | null;
  collapsed: Set<string>;
  onToggleBand: (id: string) => void;
  onSaveCell: (
    args: { metricId: string; entityId: string; year: number } & CellInput & {
        note?: string;
        footnote?: string;
        /** portal — açıklama isteğine yanıt (P3-17) */
        clientNote?: string;
        /** M17 — onaylı değeri değiştirirken neden (yalnız iç aktör) */
        changeReason?: string;
      },
  ) => void;
  /** TODO-KPI-05 — toplu yapıştırma (sırayla tekil PUT, tek refresh); yoksa `onSaveCell` ardışık */
  onSaveCells?: ((items: PasteSaveItem[]) => Promise<{ ok: number; failed: number }>) | undefined;
  saveErrors: Record<string, string>;
  highlightCode?: string | null;
  readOnly?: boolean;
  /** kanıt bağı / sabitleme sonrası sunucu hesabı (store.refreshGrid) */
  onRefresh?: () => void;
  /** K27 — çok işletmeli müşteride "Yıllar" görünümünün işletmesi (null → konsolide, salt okunur) */
  yearsEntityId?: string | null | undefined;
  /** T21 — form ↔ KPI uyuşmazlıkları (sete bağlı projeler); hücrede FORM rozeti */
  formMismatches?: KpiFormMismatch[] | undefined;
  onApplyFormValue?: ((m: KpiFormMismatch) => void) | undefined;
  /**
   * T19 — portal (müşteri) görünümü: 🔒 readonly satırlar yazılamaz, hücre editöründe iç not yok,
   * kanıt portal API'sinden, formül popover'ında "danışman tarafından girilir" (DR11), sağda "yıl durumu" kolonu. İç (danışman) akış `undefined` ile byte-eşit.
   */
  clientView?: GridClientView | undefined;
  /** Dalga 3 — hücre notu özeti (rozet) + thread penceresi; iki yüz de verir (audience ayırır) */
  notes?: GridNotes | undefined;
  /** Kırılım (spec §6.2) — bekleme seçicisi + [Seti düzenle] / [Kırılımı bu sete ekle] */
  breakdown?: GridBreakdownHandlers | undefined;
}

export function KpiGrid(props: KpiGridProps): JSX.Element {
  const { formMismatches, onApplyFormValue } = props;
  const formMismatchMap = useMemo(() => {
    const map = new Map<string, KpiFormMismatch>();
    for (const m of formMismatches ?? []) map.set(`${m.metricId}|${m.year}`, m);
    return map;
  }, [formMismatches]);
  const formMismatchFor = useCallback(
    (metricId: string, year: number) => formMismatchMap.get(`${metricId}|${year}`),
    [formMismatchMap],
  );
  const {
    grid,
    mode,
    entityYear,
    search,
    categoryId,
    collapsed,
    onToggleBand,
    onSaveCell,
    onSaveCells,
    saveErrors,
    highlightCode,
    readOnly,
    onRefresh,
    clientView,
    notes,
  } = props;
  const yearsEntityId = props.yearsEntityId ?? null;
  const totalColumns = !!clientView?.totalColumns;
  const columns = useMemo(
    () =>
      buildColumns(
        grid,
        mode,
        entityYear,
        yearsEntityId,
        totalColumns ? { totalColumns } : undefined,
      ),
    [grid, mode, entityYear, yearsEntityId, totalColumns],
  );
  const totalKind = totalKindOf(grid);
  const partialLookup = useMemo(() => buildPartialLookup(grid), [grid]); // K8 ⓘ etiketleri
  // salt okunur (konsolide) hücreye tıklanınca kısa ipucu — hücre "düzenlenebilir" gibi görünmesin
  const [readonlyHint, setReadonlyHint] = useState<string | null>(null);
  useEffect(() => {
    if (!readonlyHint) return;
    const t = setTimeout(() => setReadonlyHint(null), 3500);
    return () => clearTimeout(t);
  }, [readonlyHint]);
  const rows = useMemo(
    () => buildRows({ grid, collapsed, search, categoryId, columns }),
    [grid, collapsed, search, categoryId, columns],
  );
  const [nav, setNav] = useState<NavState>({ focus: null, editing: false });
  const [formulaFor, setFormulaFor] = useState<CellPos | null>(null);
  const [reportedFor, setReportedFor] = useState<CellPos | null>(null);
  const [noteFor, setNoteFor] = useState<CellPos | null>(null); // not penceresi (K10 + rozet)
  const [editorTab, setEditorTab] = useState<CellEditorTab>("value");
  // güvenlik/UX taraması 2026-10-08 (Codex #2): yazarak açılan editöre ilk karakter taşınır
  const [editorInitialRaw, setEditorInitialRaw] = useState<string | null>(null);
  const cellRefs = useRef(new Map<string, HTMLTableCellElement>());
  // portal (P2-11): durum kolonu sağa yapışık yalnız geniş ekranda — 390'da değer kolonlarını örtüyordu
  const wide = useMediaQuery("(min-width: 1024px)");
  const tablet = useMediaQuery("(max-width: 1024px)");
  const narrow = useMediaQuery("(max-width: 768px)"); // K9 — tablet portre: ad kolonu 160 px
  const statusCol = !!clientView && !clientView.consolidated;
  const stickyStatus = wide;
  const nameColWidth = narrow ? 160 : tablet ? 220 : 320;

  const showChangeCol =
    mode === "years" &&
    columns.filter((c) => c.kind === "year").length >= 2 &&
    grid.indicators.some((i) => i.showChange);
  const totalCols = 2 + columns.length + (showChangeCol ? 1 : 0) + (statusCol ? 1 : 0);

  // satır başına yükseklik (portal grup bandı 40 / satır 56 px) — doldurucu kayması yok
  const rowHeights = useMemo(() => gridRowHeights(rows, !!clientView), [rows, clientView]);
  const virtual = useVirtualRows(rows.length, cellRefs, rowHeights);
  const win = virtual.window;

  function keyFor(pos: CellPos): string {
    return `${pos.row}:${pos.col}`;
  }

  /** odak: hücre render dışındaysa (sanal pencere) kaydırıp sonraki boyamada odaklar */
  const focusPos = useCallback(
    (pos: CellPos, opts?: { center?: boolean; preventScroll?: boolean }): void => {
      virtual.focusCell(pos.row, keyFor(pos), cellRefs.current, opts);
    },
    [virtual],
  );

  // kural 12 — highlight: hedef satıra scroll + 2 sn vurgu; portalda (P3-15) hedef yılın hücresine
  // odak (yatay scroll dahil) — "Satıra git" sonrası odak düğmede kalmasın.
  const [flashId, setFlashId] = useState<string | null>(null);
  const highlightYear = clientView?.highlightYear ?? null;
  useEffect(() => {
    if (!highlightCode) return;
    const ind = grid.indicators.find((i) => i.code === highlightCode);
    if (!ind) return;
    const idx = rows.findIndex((r) => r.indicator.id === ind.id);
    if (idx < 0) return;
    const colIdx = Math.max(
      0,
      highlightYear !== null
        ? columns.findIndex((c) => c.year === highlightYear && !c.aggregate)
        : 0,
    );
    setNav({ focus: { row: idx, col: colIdx }, editing: false });
    setFlashId(ind.id);
    focusPos({ row: idx, col: colIdx }, { center: true, preventScroll: true });
    const t = setTimeout(() => setFlashId(null), 2000);
    return () => clearTimeout(t);
  }, [highlightCode, highlightYear, grid.indicators, rows, columns]);

  /** popover çapası: hücre elementi (her ölçümde güncel okunur — P3-17 / portal konumlandırma) */
  function anchorFor(pos: CellPos): () => HTMLElement | null {
    const key = keyFor(pos);
    return () => cellRefs.current.get(key) ?? null;
  }

  function clientEditable(pos: CellPos): boolean {
    if (!clientView) return true;
    const row = rows[pos.row];
    const col = columns[pos.col];
    if (!row || !col) return false;
    if (row.indicator.effectiveClientAccess !== "open") return false;
    const cell = readCell(row.indicator, col);
    return cell?.editable !== false;
  }

  /** Hücre not penceresi (rozet tıklaması ve değer yazılamayan hücre) */
  function openNotes(pos: CellPos): void {
    setFormulaFor(null);
    setReportedFor(null);
    setNav({ focus: pos, editing: false });
    setNoteFor(pos);
  }

  function openEditor(pos: CellPos): void {
    if (readOnly || !isEditable(rows, columns, pos) || !clientEditable(pos)) {
      const col = columns[pos.col];
      if (clientView && notes && canNoteAt(rows, columns, pos)) {
        openNotes(pos); // K10: denetçi/salt-okunur hücre — değer yazılamaz, not yazılabilir
        return;
      }
      if (col?.aggregate && !readOnly)
        setReadonlyHint(
          totalKind === "scope"
            ? KPI_COPY.scopeTotalReadonlyHint
            : KPI_COPY.consolidatedReadonlyHint,
        );
      return;
    }
    setFormulaFor(null);
    setReportedFor(null);
    setNoteFor(null);
    setEditorTab("value");
    setEditorInitialRaw(null);
    setNav({ focus: pos, editing: true });
  }

  /** T18 — ataç rozeti: aggregate (konsolide/ara toplam) kolonda kanıt yok; türetilmişte de açılır. */
  function openEvidence(pos: CellPos): void {
    const col = columns[pos.col];
    const row = rows[pos.row];
    if (!col || !row || row.isBand || col.aggregate || !col.entityId) return;
    setFormulaFor(null);
    setReportedFor(null);
    setEditorTab("evidence");
    setNav({ focus: pos, editing: true });
  }

  function openFormula(pos: CellPos): void {
    setNav({ focus: pos, editing: false });
    setReportedFor(null);
    setFormulaFor(pos);
  }

  /** Formül/kırılım bölümünden hücreye git: aynı kolonda hedef satıra odak + 2 sn vurgu */
  function jumpTo(indicatorId: string, col: number): void {
    const idx = rows.findIndex((x) => x.indicator.id === indicatorId);
    setFormulaFor(null);
    if (idx < 0) return;
    setNav({ focus: { row: idx, col }, editing: false });
    focusPos({ row: idx, col });
    setFlashId(indicatorId);
    setTimeout(() => setFlashId(null), 2000);
  }

  /** T23 — RAPORLANAN / SABİT rozeti */
  function openReported(pos: CellPos): void {
    setNav({ focus: pos, editing: false });
    setFormulaFor(null);
    setReportedFor(pos);
  }

  // TODO-KPI-05 — yapıştırma durum makinesi
  const paste = useGridPaste({
    rows,
    columns,
    focus: nav.focus,
    editing: nav.editing,
    readOnly: !!readOnly,
    clientView: !!clientView,
    onSaveCell,
    onSaveCells,
  });

  const suppressed = nav.editing || !!formulaFor || !!reportedFor || !!noteFor || !!paste.plan;
  const hover = useFormulaHover({ rows, columns, grid, cellRefs, clientView, suppressed });

  function onKeyDown(e: React.KeyboardEvent): void {
    if (nav.editing) return; // editör kendi dinler
    if (!isGridCellKeyTarget(e.target)) return; // ad hücresi düğmeleri yerel Enter/Space (I1)
    const map: Record<string, GridKey | undefined> = {
      ArrowUp: "ArrowUp",
      ArrowDown: "ArrowDown",
      ArrowLeft: "ArrowLeft",
      ArrowRight: "ArrowRight",
      Enter: "Enter",
      Escape: "Escape",
      Home: "Home",
      End: "End",
      Tab: e.shiftKey ? "ShiftTab" : "Tab",
    };
    const k = map[e.key];
    if (!k) {
      // Ctrl/⌘+V tarayıcının paste olayına bırakılır (onPaste); diğer kısayollar da dokunulmaz
      // düzenleme dışında yazmaya başlayınca direkt edit (D7)
      if (nav.focus && e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        if (isEditable(rows, columns, nav.focus)) {
          e.preventDefault();
          openEditor(nav.focus);
          setEditorInitialRaw(e.key); // ilk tuş editöre `initialRaw` olarak gider (kaybolmaz)
        } else {
          // kırılım toplamına yazma denemesi sessiz yok sayılmaz (spec §6.2)
          const hint = breakdownHintFor(grid, rows[nav.focus.row], !!clientView);
          if (hint) setReadonlyHint(hint);
        }
      }
      return;
    }
    if (!nav.focus) return;
    e.preventDefault();
    const toggle = totalToggleFor(nav, k, rows); // DR9 — ilk kolonda ←/→ aç/kapa, odak kalır
    if (toggle) {
      onToggleBand(toggle);
      return;
    }
    const next = navReducer(nav, k, rows, columns);
    if (next.editing && next.focus) {
      openEditor(next.focus);
      return;
    }
    if (k === "Enter" && next.focus && rows[next.focus.row]?.indicator.isDerived) {
      openFormula(next.focus);
      return;
    }
    setFormulaFor(null);
    setReportedFor(null);
    setNav(next);
    focusPos(next.focus!);
  }

  const focusRow = nav.focus ? rows[nav.focus.row] : undefined;
  const focusCol = nav.focus ? columns[nav.focus.col] : undefined;
  const formulaRow = formulaFor ? rows[formulaFor.row] : undefined;
  const formulaCol = formulaFor ? columns[formulaFor.col] : undefined;
  const reportedRow = reportedFor ? rows[reportedFor.row] : undefined;
  const reportedCol = reportedFor ? columns[reportedFor.col] : undefined;
  const visibleRows = win.active ? rows.slice(win.start, win.end) : rows;

  return (
    <div
      // P3-16: görünür ince yatay kaydırma çubuğu (overlay scrollbar'da 4. yıl / Değişim sütunu
      // sessizce ekran dışında kalıyordu) — globals.css `.kpi-scroll-x`.
      // Dikey: ebeveyn flex sütununda kalan alanı doldurur (sayfa `fullHeight`); eski
      // `maxHeight: calc(100vh - 320px)` sabiti kalktı — alt bilgi satırı artık tablonun altında sabit.
      ref={virtual.containerRef}
      onScroll={virtual.onScroll}
      className="kpi-scroll-x"
      data-testid="kpi-grid-scroll"
      data-virtual={win.active ? "on" : "off"}
      style={{
        position: "relative",
        flex: 1,
        minHeight: 160,
        overflowY: "auto",
      }}
    >
      <KpiGridBody
        rows={rows}
        visibleRows={visibleRows}
        columns={columns}
        win={win}
        theadRef={virtual.theadRef}
        totalKind={totalKind}
        fiscalStartMonth={grid.fiscalYearStartMonth ?? 1}
        narrow={narrow}
        nameColWidth={nameColWidth}
        showChangeCol={showChangeCol}
        statusCol={statusCol}
        stickyStatus={stickyStatus}
        totalCols={totalCols}
        onKeyDown={onKeyDown}
        onPaste={paste.onPaste}
        hoverHandlers={hover.handlers}
        focus={nav.focus}
        flashId={flashId}
        readOnly={!!readOnly}
        saveErrors={saveErrors}
        cellRefs={cellRefs}
        onToggleBand={onToggleBand}
        onFocusCell={(pos) => {
          setFormulaFor(null);
          setReportedFor(null);
          setNav({ focus: pos, editing: false });
        }}
        onOpenFormula={openFormula}
        onOpenEditor={openEditor}
        onOpenEvidence={openEvidence}
        onOpenReported={openReported}
        formMismatchFor={formMismatchFor}
        onApplyFormValue={onApplyFormValue}
        clientView={clientView}
        notes={notes}
        onOpenNotes={openNotes}
        partialLookup={partialLookup}
        isRecomputing={props.breakdown?.isRecomputing}
      />
      {hover.card}
      {readonlyHint ? (
        <div
          role="status"
          data-testid="kpi-readonly-hint"
          className="text-xs px-3 py-2"
          style={{
            position: "sticky",
            bottom: 0,
            left: 0, // yatay kaydırmada görünür kenara yapışır (yoksa tablonun soluyla birlikte kayıyordu)
            zIndex: 5,
            background: "var(--color-warning-bg)",
            color: "var(--color-warning-text)",
            borderTop: "1px solid var(--color-border)",
          }}
        >
          {readonlyHint}
        </div>
      ) : null}
      {paste.snack ? (
        <PasteSnackbar
          message={paste.snack.message}
          onUndo={paste.snack.undo ? paste.undo : undefined}
        />
      ) : null}
      {paste.plan ? (
        <KpiPasteDialog
          plan={paste.plan}
          busy={paste.busy}
          onConfirm={paste.confirm}
          onSkipEmptyRows={paste.skipEmptyRows}
          onCancel={() => {
            paste.cancel();
            if (nav.focus) focusPos(nav.focus, { preventScroll: true });
          }}
        />
      ) : null}
      {nav.editing && nav.focus && focusRow && focusCol && !focusRow.isBand && focusCol.entityId ? (
        <KpiCellEditor
          key={`${focusRow.indicator.id}:${focusCol.key}`}
          indicator={focusRow.indicator}
          cell={readCell(focusRow.indicator, focusCol)}
          columnLabel={focusCol.label}
          getAnchor={anchorFor(nav.focus)}
          initialTab={editorTab}
          {...(editorInitialRaw !== null ? { initialRaw: editorInitialRaw } : {})}
          valueTab={!focusRow.indicator.isDerived}
          evidence={{
            clientId: grid.clientId,
            // portalda çoklu bağ seçici yalnız açık (open) göstergeler (DR9)
            indicators: clientView
              ? grid.indicators.filter((i) => i.effectiveClientAccess === "open")
              : grid.indicators,
            years: grid.years,
            entityId: focusCol.entityId,
            year: focusCol.year,
            readOnly: !!readOnly || (!!clientView && !clientEditable(nav.focus)),
            onChanged: () => onRefresh?.(),
            ...(clientView ? { portalSetId: clientView.setId } : {}),
          }}
          isClient={!!clientView}
          priorCell={focusRow.indicator.cells[focusCol.entityId]?.[focusCol.year - 1]}
          unitChanged={clientView?.unitChanged}
          notes={notes}
          onOpenNotes={notes ? () => openNotes(nav.focus!) : undefined}
          breakdown={breakdownPropsFor(grid, focusRow, focusCol, !!clientView, {
            ...props.breakdown,
            onJumpTo: (id) => jumpTo(id, nav.focus!.col),
          })}
          formLink={
            // Finar 14.4 — FORM rozetiyle aynı eşleşme; yazım varsayılan işletmeye (C8)
            !clientView && !readOnly && onApplyFormValue && !focusCol.aggregate
              ? (() => {
                  const m = formMismatchFor(focusRow.indicator.metricId, focusCol.year);
                  return m?.entityId === focusCol.entityId ? m : undefined;
                })()
              : undefined
          }
          onSave={(input) => {
            const entityId = focusCol.entityId;
            if (!entityId) return;
            onSaveCell({
              metricId: focusRow.indicator.metricId,
              entityId,
              year: focusCol.year,
              ...input,
            });
            const next = navReducer(nav, "Enter", rows, columns);
            setNav(next);
            focusPos(next.focus!);
          }}
          onCancel={() => {
            setNav({ focus: nav.focus, editing: false });
            focusPos(nav.focus!);
          }}
        />
      ) : null}
      {noteFor && notes ? (
        <KpiCellNoteEditor
          key={`${noteFor.row}:${noteFor.col}`}
          audience={notes.audience}
          setId={notes.setId}
          target={{ rows, columns, pos: noteFor }}
          getAnchor={anchorFor(noteFor)}
          onChanged={notes.onChanged}
          onClose={() => {
            setNoteFor(null);
            focusPos(noteFor, { preventScroll: true });
          }}
        />
      ) : null}
      {formulaFor && formulaRow && formulaCol ? (
        <FormulaPopover
          // hücre başına yeniden kurulur: çapa + varyant/kaynak durumu önceki hücreden taşınmaz
          key={`${formulaRow.indicator.id}:${formulaCol.key}`}
          indicator={formulaRow.indicator}
          cell={readCell(formulaRow.indicator, formulaCol)}
          columnLabel={formulaCol.label}
          getAnchor={anchorFor(formulaFor)}
          inputs={formulaInputsFor(formulaRow.indicator, grid, formulaCol).inputs}
          hiddenInputs={
            clientView ? formulaInputsFor(formulaRow.indicator, grid, formulaCol).hidden : []
          }
          labelByCode={
            clientView ? formulaInputsFor(formulaRow.indicator, grid, formulaCol).labels : undefined
          }
          partialLookup={partialLookup}
          wavgPairs={
            clientView ? undefined : wavgPopoverPairs(formulaRow.indicator, grid, formulaCol)
          }
          // P2-12 (danışman): sabit/raporlanan hücrede popover'dan ReportedPopover'a geçiş (sabiti kaldır)
          onOpenReported={clientView ? undefined : () => openReported(formulaFor)}
          // 8.1 (2026-10-08): raporlanan değer henüz yokken giriş alanı Kanıt sekmesinin üstünde
          onEnterReported={clientView ? undefined : () => openEvidence(formulaFor)}
          onJumpTo={(indicatorId) => jumpTo(indicatorId, formulaFor.col)}
          breakdown={breakdownPropsFor(grid, formulaRow, formulaCol, !!clientView, {
            ...props.breakdown,
            onJumpTo: (id) => jumpTo(id, formulaFor.col),
            onAddReported: clientView ? undefined : () => openEvidence(formulaFor),
          })}
          onOpenNotes={
            notes && canNoteAt(rows, columns, formulaFor) ? () => openNotes(formulaFor) : undefined
          }
          onClose={() => setFormulaFor(null)}
        />
      ) : null}
      {reportedFor && reportedRow && reportedCol ? (
        <ReportedPopover
          key={`${reportedRow.indicator.id}:${reportedCol.key}`}
          indicator={reportedRow.indicator}
          cell={readCell(reportedRow.indicator, reportedCol)}
          columnLabel={reportedCol.label}
          getAnchor={anchorFor(reportedFor)}
          // portalda sabitleme yok (K14 — raporlanan değer kararı danışmanın)
          readOnly={!!readOnly || !!clientView}
          {...(clientView ? { portalSetId: clientView.setId } : {})}
          onPinned={() => onRefresh?.()}
          onEdit={clientView || readOnly ? undefined : () => openEvidence(reportedFor)}
          onClose={() => {
            setReportedFor(null);
            if (reportedFor) focusPos(reportedFor);
          }}
        />
      ) : null}
    </div>
  );
}
